import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import {
  Avatar,
  Chip,
  Container,
  Drawer,
  InputBase,
  Link,
  Stack,
  TableHead,
  TableSortLabel,
  Tooltip,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import KeyIcon from '@mui/icons-material/Key'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import AddNewUser from './addNewUser'
import { useEffect, useState } from 'react'
import ViewUser from './viewUser'
import EditUserDetails from './editUserDetails'
import EditUserRoleDetails from './editUserRoleDetails'
import {
  masterUserDeleteApi,
  masterUserListApi,
} from '../../../api/master/masterAPI'
import jwt_decode from 'jwt-decode'
import SkeletonTableLoader from '../../../common/components/loaders/skeletonLoader'
import { useLocation, useSearchParams } from 'react-router-dom'
import ConfirmationModal from '../../../common/components/custom/modal/modal'
import LoadingSection from '../../../common/components/loaders/loader'
import NoDataFoundCard from '../../../common/components/cards/noDataFoundCard'
import ReLoginPage from './relogin'
import { createRef } from 'react'
import ResetPasswordModal from '../../../common/components/custom/modal/ResetPasswordModal'
import { setUsersList } from '../../../store/slice/userManagementSlice'
import {
  checkPermission,
  getCompanyLevel,
  getComparator,
  normalizeApiResponse,
  stableSort,
} from '../../../helpers/HelperFunctions'
import { PERMISSIONS } from '../../../helpers/Constants'
import CancelIcon from '@mui/icons-material/Cancel'
import { Add as AddIcon } from '@mui/icons-material'
import DrawerHeader from '../../../common/components/drawer/DrawerHeader'
import { useUserManagementRedux } from '../UserManagement'
import ThemeButton from '../../../common/components/button/Button'
import { useToastContext } from '../../../App'
import { useCommonRedux } from '../../../store/middlewares/CommonRedux'
import FilterListIcon from '@mui/icons-material/FilterList';
import useTableFilters from '../../../common/components/tableFilter/useTableFilters'
import GlobalSearch from '../../../common/components/tableFilter/GlobalSearch'
import ColumnFilter from '../../../common/components/tableFilter/ColumnFilter'
// ──────────────────────────────────────────────────────────────
// Table Column configuration
// ──────────────────────────────────────────────────────────────
const tableColumns = [
  { key: 'fullName', label: 'Full Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'roleName', label: 'Assigned Roles' },
];
function UsersTable(props) {
  const { dispatch, state: commonState } = useCommonRedux()
  const { state: _state } = useUserManagementRedux()
  const { toast } = useToastContext()
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(5)
  const [rowsList, setRowsList] = React.useState([])
  const [edited, setEdited] = React.useState('')
  const [userDetails, setUserDetails] = React.useState('')
  const [loader, setLoader] = React.useState(false)
  const [submitLoader, setSubmitLoader] = React.useState(false)
  const [resetDetails, setResetDetails] = React.useState()
  const [searchParams] = useSearchParams()
  const [state, setState] = React.useState({
    right: false,
  })
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  const pageTopRef = createRef()

  const [order, setOrder] = React.useState('asc')
  const [orderBy, setOrderBy] = React.useState()
  const {
    searchTerm,
    setSearchTerm,
    filteredData,
    uniqueValues,
    filterAnchorEl,
    setFilterAnchorEl,
    currentColumn,
    openFilter,
    closeFilter,
    toggleFilter,
    clearColumnFilter,
    isFilterActive,
    filters,
  } = useTableFilters(rowsList.map((u) => ({
    ...u,
    fullName: `${u.firstName} ${u.lastName}`,
    roles: u.roles?.map((r) => r.roleName).join(', '),
  })), tableColumns);
  const currentLabel = tableColumns.find((c) => c.key === currentColumn)?.label || currentColumn || '';

  const sortedRows = React.useMemo(() => {
    return stableSort(filteredData, getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }
  let inputHandler = (e) => {
    const lowerCase = e.target.value.toLowerCase()
    const filteredData = _state.usersList.filter((el) => {
      if (lowerCase === '') {
        return el
      } else {
        return (
          el?.firstName.toLowerCase().includes(lowerCase) ||
          el?.lastName.toLowerCase().includes(lowerCase) ||
          el?.phone?.toString().includes(lowerCase) ||
          el?.email.toLowerCase().includes(lowerCase)
        )
      }
    })
    setRowsList(filteredData)
    setPage(0)
  }
  const handleClose = () => {
    setIsOpen(false)
    setConfirmReset(false)
  }
  const handleDelete = (id) => {
    setSubmitLoader(true)
    masterUserDeleteApi(
      id,
      getCompanyLevel(
        searchParams.get('company_id'),
        location.pathname.slice(1)
      )
    )
      .then((response) => {
        setSubmitLoader(false)
        toast.showToast(response.data.message, 'success')
        getDecodeValue()
        handleClose()
        toggleDrawer('right', false)
      })
      .catch((error) => {
        setSubmitLoader(false)
        toast.showToast(normalizeApiResponse(error)?.message, 'error')
      })
  }
  const getUserList = (id) => {
    setLoader(true)
    masterUserListApi(
      id,
      getCompanyLevel(
        searchParams.get('company_id'),
        location.pathname.slice(1)
      )
    )
      .then((response) => {
        setLoader(false)
        const sortedData = response.data.result.sort((a, b) =>
          a.firstName.localeCompare(b.firstName)
        )
        dispatch(setUsersList(sortedData))
        setRowsList(sortedData)
      })
      .catch((error) => {
        setLoader(false)
        toast.showToast(normalizeApiResponse(error)?.message, 'error')
      })
  }

  const getDecodeValue = () => {
    const decodeToken = localStorage.getItem('sd_cp.jwt:tkn')
    const decoded = jwt_decode(decodeToken)
    setResetDetails(decoded)
    if (decoded) {
      let companyId = searchParams.get('company_id')
      if (!companyId) {
        companyId = decoded?.company_id
      }
      getUserList(companyId)
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    pageTopRef.current.scrollTop = 0
  }
  const gotoDrawer = (event, details) => {
    if (details) {
      setUserDetails(details)
    }
    setEdited(event)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value))
    setPage(0)
    pageTopRef.current.scrollTop = 0
  }

  const toggleDrawer = (anchor, open, switchVal, details, load) => {
    // if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
    //     return;
    // }
    if (load) {
      getDecodeValue()
    }
    setUserDetails(details)
    setEdited(switchVal)
    setState({ ...state, [anchor]: open })
  }

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - rowsList?.length)
      : 0

  useEffect(() => {
    getDecodeValue()
  }, [])

  const checkAddEdit = (item, index) => {
    switch (item) {
      case 'add':
        return <AddNewUser toggleDrawer={toggleDrawer} />
      case 'view':
        return (
          <>
            <ViewUser userId={userDetails} gotoDrawer={gotoDrawer}></ViewUser>
          </>
        )
      case 'editRole':
        return (
          <>
            <EditUserRoleDetails
              toggleDrawer={toggleDrawer}
              userId={userDetails}
              gotoDrawer={gotoDrawer}
            ></EditUserRoleDetails>
          </>
        )
      case 'editUser':
        return (
          <>
            <EditUserDetails
              toggleDrawer={toggleDrawer}
              userId={userDetails}
              gotoDrawer={gotoDrawer}
            ></EditUserDetails>
          </>
        )
    }
  }

  const DrawerActions = ({ commonState, edited, gotoDrawer, toggleDrawer }) => {
    const canUpdate = checkPermission(
      [
        PERMISSIONS.masterUserUpdate,
        PERMISSIONS.serviceproviderUserUpdate,
        PERMISSIONS.resellerUserUpdate,
        PERMISSIONS.customerUserUpdate,
      ],
      commonState.currentCompanyDetails
    )
    return (
      <div>
        {canUpdate && ['editRole', 'editUser'].includes(edited) && (
          <Tooltip title="Back">
            <IconButton
              aria-label="back"
              onClick={() => gotoDrawer('view')}
              sx={{ ml: '-2px', float: 'left', borderRadius: 0 }}
            >
              <KeyboardBackspaceIcon />
            </IconButton>
          </Tooltip>
        )}

        {edited != 'add' && (
          <Tooltip title="Close">
            <IconButton
              aria-label="delete"
              onClick={() => toggleDrawer('right', false)}
              sx={{ float: 'right', borderRadius: 0 }}
            >
              <CancelIcon color="error" size="large" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    )
  }
  const DrawerHeaderSection = ({
    edited,
    userDetails,
    resetDetails,
    setConfirmReset,
    setIsOpen,
    commonState,
  }) => {
    const canUpdate = checkPermission(
      [
        PERMISSIONS.masterUserUpdate,
        PERMISSIONS.serviceproviderUserUpdate,
        PERMISSIONS.resellerUserUpdate,
        PERMISSIONS.customerUserUpdate,
      ],
      commonState.currentCompanyDetails
    )
    const canDelete = checkPermission(
      [
        PERMISSIONS.masterUserDelete,
        PERMISSIONS.serviceproviderUserDelete,
        PERMISSIONS.resellerUserDelete,
        PERMISSIONS.customerUserDelete,
      ],
      commonState.currentCompanyDetails
    )

    if (edited == 'view') {
      return (
        <h3 style={{ display: 'flex', margin: '10px' }}>
          <Avatar sx={{ bgcolor: 'skyblue' }}>
            {userDetails?.firstName?.slice(0, 1)}
          </Avatar>
          <div style={{ marginLeft: '10px' }}>
            {userDetails?.firstName} {userDetails?.lastName}
            <div style={{ display: 'flex' }}>
              {canUpdate && resetDetails?.is_primary_contact && (
                <div
                  style={{ fontSize: 15, color: '#000000a1', cursor: 'pointer' }}
                  onClick={() => setConfirmReset(true)}
                >
                  <KeyIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} /> Reset Password
                </div>
              )}
              {canDelete && (
                <div
                  style={{
                    fontSize: 15,
                    color: '#000000a1',
                    cursor: 'pointer',
                    marginLeft: '15px',
                  }}
                  onClick={() => setIsOpen(true)}
                >
                  <PersonRemoveIcon sx={{ fontSize: 12, mr: 0.5 }} /> Delete User
                </div>
              )}
            </div>
          </div>
        </h3>
      )
    }

    if (['editRole', 'editUser'].includes(edited)) {
      return (
        <h3 style={{ display: 'flex', marginTop: '-5px', marginBottom: 0 }}>
          <div style={{ marginLeft: '10px' }}>
            {edited == 'editRole' ? 'Assign Role' : ''}
            <div style={{ fontSize: 15, color: '#000000a1' }}>
              {edited == 'editRole'
                ? `Update roles of ${userDetails?.email}`
                : 'Update user details'}
            </div>
          </div>
        </h3>
      )
    }

    if (edited == 'add') {
      return <DrawerHeader header="Add New User" onClick={() => toggleDrawer('right', false)} />
    }

    return null
  }

  return (
    <Container maxWidth="xl">
      {submitLoader && <LoadingSection />}
      <React.Fragment>
        <Drawer
          PaperProps={{ sx: { width: '25%' } }}
          variant="persistent"
          hideBackdrop
          anchor="right"
          open={state['right']}
        >
          <DrawerActions
            commonState={commonState}
            edited={edited}
            gotoDrawer={gotoDrawer}
            toggleDrawer={toggleDrawer} />

          <DrawerHeaderSection
            edited={edited}
            userDetails={userDetails}
            resetDetails={resetDetails}
            setConfirmReset={setConfirmReset}
            setIsOpen={setIsOpen}
            commonState={commonState}
          />
          {checkAddEdit(edited)}
          <ConfirmationModal
            handleConfirm={() => handleDelete(userDetails?.id)}
            title="Delete User Confirmation"
            content={
              <span>
                Are you sure you want to delete <b>{userDetails?.email}</b>?
              </span>
            }
            isOpen={isOpen}
            handleClose={handleClose}
          />
          <ResetPasswordModal
            title="Reset Password"
            topValue="25%"
            content={
              <ReLoginPage
                userDetails={userDetails}
                resetDetails={resetDetails}
                handleClose={handleClose}
              />
            }
            isOpen={confirmReset}
            hideActionsBtn
            handleClose={handleClose}
          />
        </Drawer>

      </React.Fragment>
      {/* Search and Add button */}
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5em',
          marginTop: '0.5em',
        }}
      >
        <GlobalSearch
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          placeholder="Search by Name or Email"
        />
        {checkPermission(
          [
            PERMISSIONS.masterUserCreate,
            PERMISSIONS.serviceproviderUserCreate,
            PERMISSIONS.resellerUserCreate,
            PERMISSIONS.customerUserCreate,
          ],
          commonState.currentCompanyDetails
        ) && (
            <Tooltip title="Add New User">
              <ThemeButton
                text="Add New User"
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                onClick={() => toggleDrawer('right', true, 'add')}
              />
            </Tooltip>
          )}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        ref={pageTopRef}
        sx={{
          height:
            rowsPerPage === 5 || rowsList?.length <= 7
              ? 'max-content'
              : 'calc(95vh - 200px)',
        }}
      >
        <Table
          stickyHeader
          sx={{ minWidth: 500 }}
          aria-label="custom pagination table"
        >
          <TableHead className="thead">
            <TableRow>
              <TableCell>S.No.</TableCell>
              {tableColumns.map((col) => (
                <TableCell key={col.key}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, col.key)}
                    >{col.label}</TableSortLabel>
                    <IconButton
                      size="small"
                      onClick={(e) => openFilter(e, col.key)}
                      color={isFilterActive(col.key) ? 'primary' : 'default'}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                    {isFilterActive(col.key) && (
                      <Chip size="small"
                        label={(filters?.[col.key]?.length || 0)}
                        //  label={filters[col.key]?.length  || 0} 
                        color="primary" />
                    )}
                  </Box>
                </TableCell>
              ))}
              {/* <TableCell>Full Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Assigned Roles</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody className="tbody">
            {loader ? (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}>
                  <SkeletonTableLoader />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {(rowsPerPage > 0
                  ? filteredData?.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  : filteredData
                )?.map((row, index) => (
                  <TableRow hover key={index}>
                    <TableCell width={'10px'}>
                      {page * rowsPerPage + (index + 1)}
                    </TableCell>
                    <TableCell
                      scope="row"
                      onClick={() => toggleDrawer('right', true, 'view', row)}
                    >
                      {/* <Link> {`${row.firstName} ${row.lastName}`} </Link> */}
                      <Link> {row.fullName} </Link>
                    </TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {/* {row?.roles?.map((text, index) => (
                          <Chip
                            key={index}
                            label={text.roleName}
                            variant="outlined"
                          />
                        ))} */}
                        {row.roles?.split(', ').map((role, i) => (
                          <Chip key={i} label={role} variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
                {filteredData?.length === 0 ? (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} style={{ border: 'none' }}>
                      <h3
                        style={{
                          margin: '0px auto',
                          textAlign: 'center',
                        }}
                      >
                        <NoDataFoundCard
                          messages="No Users found"
                          borderNone={true}
                        />
                      </h3>
                    </TableCell>
                  </TableRow>
                ) : (
                  ''
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" justifyContent="space-between">
        {!loader && (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start', px: 2 }}>
            <Chip
              label={
                <>
                  <strong>{filteredData.length}</strong> / {rowsList.length} records
                </>
              }
              color={filteredData.length < rowsList.length ? 'primary' : 'default'}
              variant="outlined"
              size="small"
            />
            {(filters && Object.keys(filters).length > 0 || searchTerm.trim()) && (
              <Chip
                label="Filtered"
                color="secondary"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
          component="div"
          count={filteredData?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Stack>
      <ColumnFilter
        anchorEl={filterAnchorEl}
        onClose={closeFilter}
        columnLabel={currentLabel}
        uniqueValues={uniqueValues[currentColumn] || []}
        selectedValues={filters ? filters[currentColumn] : []}
        onToggleValue={toggleFilter}
        onClear={clearColumnFilter}
      />
    </Container>
  )
}
export default UsersTable
